"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiMapPin, PiPhone, PiEnvelopeSimple, PiPaperPlaneRight, PiCheckCircle } from "react-icons/pi";

export default function OvanContact({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const [isDark, setIsDark] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addresses: string[] = content.addresses || (content.address ? [content.address] : ["C/54 Northwest Freeway, \nHouston, USA 485"]);
  const emails: string[] = content.emails || (content.email ? [content.email] : ["contact@example.com"]);
  const phones: string[] = content.phones || (content.phone ? [content.phone] : ["+152 538-4567"]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
      };
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    }, 1000);
  };

  return (
    <section
      id="contact"
      className={`relative py-16 md:py-24 transition-colors duration-200 ${
        isDark ? "bg-slate-900" : "bg-white"
      } ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""}`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center pb-12 max-w-xl mx-auto">
          <span className="py-1 px-3 rounded-xl inline-flex bg-blue-600/10 text-blue-500 font-semibold uppercase text-xs mb-3">
            Contact Us
          </span>
          <h2 className={`mb-4 text-2xl md:text-3xl leading-normal font-semibold transition-colors ${
            isDark ? "text-white" : "text-slate-900"
          }`}>
            <EditableText
              value={content.title || "Let's Talk About Your Project"}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, title: val })
              }
              as="span"
              className={isDark ? "text-white" : "text-slate-900"}
              placeholder="Contact Title"
            />
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            <EditableText
              value={
                content.text ||
                "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"
              }
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, text: val })
              }
              as="span"
              placeholder="Contact Subtitle"
            />
          </p>
        </div>

        {/* Grid of 3 Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-6">
          
          {/* Location Card */}
          <div className={`p-6 sm:p-7 rounded-[24px] shadow-[0_8px_30px_rgba(0,0,0,0.025)] hover:shadow-lg transition-all duration-300 flex items-start gap-4 border ${
            isDark ? "bg-slate-800/40 border-slate-800/80" : "bg-white border-slate-50"
          }`}>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-600/10 text-blue-500">
              <PiMapPin className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={`text-base font-bold tracking-tight leading-snug mb-1 ${
                isDark ? "text-white" : "text-slate-900"
              }`}>
                <EditableText
                  value={content.addressTitle || "Our Office"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, addressTitle: val })
                  }
                  as="span"
                  placeholder="Address Title"
                />
              </h4>
              <div className="space-y-1.5">
                {addresses.map((addr: string, idx: number) => (
                  addr.trim() && (
                    <a 
                      key={idx}
                      href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[13px] text-slate-400 hover:text-blue-500 transition-colors leading-relaxed font-medium block"
                    >
                      {addr}
                    </a>
                  )
                ))}
              </div>
            </div>
          </div>
 
          {/* Email Card */}
          <div className={`p-6 sm:p-7 rounded-[24px] shadow-[0_8px_30px_rgba(0,0,0,0.025)] hover:shadow-lg transition-all duration-300 flex items-start gap-4 border ${
            isDark ? "bg-slate-800/40 border-slate-800/80" : "bg-white border-slate-50"
          }`}>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-600/10 text-blue-500">
              <PiEnvelopeSimple className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={`text-base font-bold tracking-tight leading-snug mb-1 ${
                isDark ? "text-white" : "text-slate-900"
              }`}>
                <EditableText
                  value={content.emailTitle || "Email Address"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, emailTitle: val })
                  }
                  as="span"
                  placeholder="Email Title"
                />
              </h4>
              <div className="text-[13px] text-slate-400 leading-relaxed font-medium space-y-1.5">
                {emails.map((mail: string, idx: number) => (
                  mail.trim() && (
                    <a key={idx} href={`mailto:${mail.trim()}`} className="hover:text-blue-500 transition-colors block truncate">
                      {mail}
                    </a>
                  )
                ))}
              </div>
            </div>
          </div>
 
          {/* Phone Card */}
          <div className={`p-6 sm:p-7 rounded-[24px] shadow-[0_8px_30px_rgba(0,0,0,0.025)] hover:shadow-lg transition-all duration-300 flex items-start gap-4 border ${
            isDark ? "bg-slate-800/40 border-slate-800/80" : "bg-white border-slate-50"
          }`}>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-600/10 text-blue-500">
              <PiPhone className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={`text-base font-bold tracking-tight leading-snug mb-1 ${
                isDark ? "text-white" : "text-slate-900"
              }`}>
                <EditableText
                  value={content.phoneTitle || "Phone Number"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, phoneTitle: val })
                  }
                  as="span"
                  placeholder="Phone Title"
                />
              </h4>
              <div className="text-[13px] text-slate-400 leading-relaxed font-medium space-y-1.5">
                {phones.map((num: string, idx: number) => (
                  num.trim() && (
                    <a key={idx} href={`tel:${num.replace(/\s+/g, "")}`} className="hover:text-blue-500 transition-colors block">
                      {num}
                    </a>
                  )
                ))}
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
